import { Fragment, useState } from "react";
import {
  ExceptionStatus,
  FindingsOrderField,
  OrderDirection,
  ScoreState,
} from "~/operations";
import {
  Checkbox,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { DataTable } from "~/components/data-table";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { CheckboxCell, RiskFactorsCell } from "~/components/Cells";
import { AssetCell } from "~/components/FirewatchRow/AssetCell";
import { Header } from "~/components/FirewatchPage/FirewatchPageColumns";
import {
  ExposedAsset,
  AssetContextualLinkType,
  SelectionProps,
} from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { useAssetContextualLink } from "~/pages/space/vulnerabilities/components/exposed-assets/hooks/useAssetContextualLink";
import { FirstFoundCell } from "~/components/FirewatchRow/FirstFoundCell";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { Paginate, PaginateProps } from "~/components/pagination/paginate";
import {
  ASSET_TABLE_INITIAL_PAGE_RANGE,
  Pagination,
  PaginationRange,
  UsePaginationProps,
} from "~/components/pagination";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { ActionCell } from "~/components/FirewatchRow/ActionCell";

type HeaderObjects = {
  [key: string]: Header;
};

interface ExposedAssetsTableProps {
  assets: Array<ExposedAsset> | undefined;
  selectionProps?: SelectionProps;
  paginationProps?: PaginateProps;
  sort: ReturnType<typeof useSort>;
  filteredTotalCount: number;
  fetchMore?: UsePaginationProps["fetchMore"];
  pageInfo?: UsePaginationProps["pageInfo"];
  urlContextType: (typeof AssetContextualLinkType)[keyof typeof AssetContextualLinkType];
  contextId: string;
  scopeParams: URLSearchParams;
}

export const ExposedAssetsTable = ({
  selectionProps,
  paginationProps,
  assets = [],
  sort,
  filteredTotalCount,
  fetchMore,
  pageInfo,
  urlContextType,
  contextId,
  scopeParams,
}: ExposedAssetsTableProps) => {
  const navigate = useNavigate();
  const [pageItems, setPageItems] = useState<PaginationRange>(
    ASSET_TABLE_INITIAL_PAGE_RANGE,
  );

  const TableColumns: HeaderObjects = {
    Select: {
      id: "SELECT",
      field: "SELECT",
      label: (
        <Checkbox
          checked={
            selectionProps?.selectedEntities?.length === (assets?.length || 0)
          }
          indeterminate={
            (selectionProps?.selectedEntities?.length || 0) > 0 &&
            (selectionProps?.selectedEntities?.length || 0) <
              (assets?.length || 0)
          }
          onChange={(_, checked) => selectionProps?.onCheckAll(checked, assets)}
        />
      ),
      sortable: false,
      options: {},
    },
    Impact: {
      id: "IMPACT",
      field: FindingsOrderField.RiskValue, // Risk value is the appropriate field for impact component for sorting purposes
      label: "Risk",
      sortable: true,
      options: { width: 130 },
    },
    Asset: {
      id: FindingsOrderField.AssetName,
      field: FindingsOrderField.AssetName,
      label: "Asset",
      sortable: true,
    },
    RiskFactors: {
      id: FindingsOrderField.RiskFactors,
      field: FindingsOrderField.RiskFactors,
      label: "Risk factors",
      sortable: true,
      options: { width: 180 },
    },
    LastUpdated: {
      id: FindingsOrderField.LastUpdated,
      field: FindingsOrderField.LastUpdated,
      label: "Last updated",
      sortable: true,
      options: { width: 180 },
    },
    Action: {
      id: "ACTION",
      field: "ACTION",
      label: "Action",
      sortable: false,
      options: { width: 100 },
    },
  };

  const tableHeaders =
    [
      selectionProps ? TableColumns.Select : undefined,
      TableColumns.Impact,
      TableColumns.Asset,
      TableColumns.RiskFactors,
      TableColumns.LastUpdated,
      TableColumns.Action,
    ].flatMap((e) => e ?? []) || [];

  const assetRows = paginationProps
    ? assets
    : assets.slice(pageItems.from, pageItems.to);

  return (
    <Fragment>
      <DataTable
        id="affected-assets-list"
        selectable={
          selectionProps ? selectionProps.hasSelectPermissions : false
        }
        selection={selectionProps?.selectedEntities}
      >
        <TableHead>
          <TableRow>
            {tableHeaders.map((header) => {
              return (
                <TableCell
                  key={header.field}
                  align={header.options?.textAlign}
                  width={header.options?.width}
                  sx={header.options?.sx}
                >
                  {header.sortable ? (
                    <TableSortLabel
                      component={sort.baseUrl ? RouterLink : "span"}
                      to={
                        sort.baseUrl
                          ? sort.sortColumnUrl(header.field).href
                          : undefined
                      }
                      onClick={
                        sort.baseUrl
                          ? () => sort.handleSortClick(header.field)
                          : undefined
                      }
                      direction={
                        sort.orderBy.direction === OrderDirection.Asc
                          ? "asc"
                          : "desc"
                      }
                      active={header.field === sort.orderBy.field}
                    >
                      {header.label}
                    </TableSortLabel>
                  ) : (
                    header.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {assetRows.map((asset) => {
            const { contextUrl } = useAssetContextualLink({
              assetId: asset.id,
              type: urlContextType,
              contextId: contextId,
              scopeParams: scopeParams,
            });
            const riskFactors = asset.riskFactors;
            const score = asset.score;
            const isPendingException =
              asset?.exception?.status === ExceptionStatus.New;
            const isRiskAccepted = asset.state === ScoreState.RiskAccepted;
            const isDisabled = asset.state === ScoreState.Disabled;
            const isFalsePositive = asset.state === ScoreState.FalsePositive;
            const hasException =
              isPendingException || isRiskAccepted || isDisabled;

            return (
              <TableRow
                key={asset.id}
                onClick={() => navigate(contextUrl)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "background.light",
                  },
                }}
                className="asset-row"
              >
                {selectionProps && (
                  <CheckboxCell
                    onClick={(e) => selectionProps?.onCheckboxClick(e)}
                    onChange={(e, changed) =>
                      selectionProps?.onCheckboxChange(e, changed, asset)
                    }
                    checked={selectionProps.getIsRowSelected(
                      asset?.assetMrn || "",
                    )}
                  />
                )}
                {/* Risk */}
                <TableCell>
                  <ImpactUpdated
                    impact={{ value: score, rating: asset.rating }}
                    isRiskScore
                    isActive={!hasException}
                  />
                </TableCell>

                {/* Asset */}
                <AssetCell
                  title={asset.title}
                  iconId={asset.iconId}
                  tags={asset.tags}
                  isPendingException={isPendingException}
                  isRiskAccepted={isRiskAccepted}
                  isFalsePositive={isFalsePositive}
                  isDisabled={isDisabled}
                />

                {/* Risk Factors */}
                <RiskFactorsCell
                  riskFactors={riskFactors}
                  isActive={!hasException}
                />

                {/* Last Updated */}
                <FirstFoundCell
                  discoveryTime={asset.lastUpdated}
                  isActive={!hasException}
                />
                {/* Action */}
                <ActionCell row={asset} scopeMrn={asset.assetMrn} />
              </TableRow>
            );
          })}
        </TableBody>
      </DataTable>
      {/* Not every endpoint used for an AffectedAssets list supports backwards pagination */}
      {/* This component needs to support both pagination patterns in the meantime */}
      {paginationProps && <Paginate {...paginationProps} />}
      {!paginationProps && fetchMore && pageInfo && (
        <Pagination
          fetchMore={fetchMore}
          totalCount={filteredTotalCount || 0}
          setPageItems={setPageItems}
          pageInfo={pageInfo}
          defaultPageSize={ASSET_TABLE_INITIAL_PAGE_RANGE.to}
        />
      )}
    </Fragment>
  );
};
